import { useEffect } from 'react';
import { useSelector, useDispatch, useStore } from 'react-redux';
import { generateLoadingStateKey } from '../store/api/reducer';

/**
 * An action is either a string ('FOO/INDEX') or an object
 * ({ type: 'FOO/INDEX', args }). Both loading and error state are keyed by
 * generateLoadingStateKey(type, args).
 */
const normalizeAction = (action) => {
  const isObject = typeof action === 'object' && action !== null;
  const actionType = isObject ? action.type : action;
  const args = isObject ? action.args : undefined;
  return { actionType, args, key: generateLoadingStateKey(actionType, args) };
};

/**
 * Aggregate loading state for a list of resource actions. Returns true while
 * any of them is loading. An action whose loading state has never been
 * tracked is kicked off by dispatching `${type}_REQUEST_BEGIN`, and counts as
 * loading until that lands. When sending args to an action, pass them along
 * to indexRequestSuccess on completion so its key resolves:
 *   actions.indexRequestSuccess({ ...normalized, args: payload.args })
 *
 * Both selectors cover the whole list, so the hook count stays constant when
 * `actions` changes length between renders (FORMUE-WEB-5XX).
 */
export const useResource = (actions = []) => {
  const dispatch = useDispatch();
  const store = useStore();
  const normalized = actions.map(normalizeAction);

  // A string, so the effect below re-runs only when this list's set of
  // untracked keys changes — including keys a FLUSH drops while mounted.
  const untracked = useSelector((state) =>
    normalized
      .filter(({ key }) => !state.api.loading.hasOwnProperty(key))
      .map(({ key }) => key)
      .join('\n')
  );

  // Dispatching during render updates every other subscribed component
  // mid-render (React's setState-in-render error), so begin from an effect.
  // Each dispatch notifies synchronously, so check the live store to begin a
  // request only once.
  useEffect(() => {
    normalized.forEach(({ actionType, args, key }) => {
      if (!store.getState().api.loading.hasOwnProperty(key)) {
        dispatch({ type: `${actionType}_REQUEST_BEGIN`, payload: { args } });
      }
    });
    // normalized is rebuilt every render; untracked is its stable summary.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [untracked, dispatch, store]);

  return useSelector((state) =>
    normalized.some(
      ({ key }) =>
        !state.api.loading.hasOwnProperty(key) || state.api.loading[key]
    )
  );
};

export const useErrorState = (actions = []) =>
  // Single useSelector for the same constant-hook-count reason as above.
  useSelector((state) =>
    actions.some((action) =>
      Boolean(state.api.error[normalizeAction(action).key])
    )
  );
